import React, { FC, useEffect, useState } from 'react';
import { InventoryItemData } from './inventoryItemData';
import styles from './styles.module.css';
import { ReactComponent as InventoryItemPriceIcon } from '../../../assets/icons/inventory-item-price.svg';
import { ReactComponent as InventoryItemQuantityIcon } from '../../../assets/icons/inventory-item-quantity.svg';
import { IInventoryItem } from './types';
import { Switch, ToastMessage } from '@cokitchen/cokitchen-components';
import TurnOffMeal from '../inventory-category/modals/turnOffMeal';
import { apiService } from '../../../utils/api';
import moment from 'moment';
import { toast } from 'react-toastify';

type Props = {
  handleMealUpdate: (meal: IInventoryItem) => void;
  meal: IInventoryItem;
  inlineEdit: boolean;
  onMealEdited: (meal: IInventoryItem) => void;
  brandName: string;
  workingHours: Array<{
    day: string;
    opening_time: string;
  }>;
};

const InventoryItem: FC<Props> = ({
  meal,
  inlineEdit,
  handleMealUpdate,
  onMealEdited,
  brandName,
  workingHours
}: Props) => {
  const [editableMeal, setEditableMeal] = useState({ ...meal });
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [active, setIsActive] = useState(false);
  const [timeleft, setTimeleft] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      const now = moment();
      const then = moment(editableMeal.availability_soon_date_);
      if (
        editableMeal.available_no === 'INFINITE' &&
        (editableMeal.availability_soon_date_ === null || now.isAfter(then))
      ) {
        setIsActive(true);
      } else {
        setIsActive(false);
      }
      if (editableMeal.availability_soon_date_ != null) {
        const diffInMilliseconds = then.diff(now);
        const diffInSeconds = Math.floor(diffInMilliseconds / 1000);
        const minutes = Math.floor(diffInSeconds / 60);
        const seconds = diffInSeconds % 60;

        setTimeleft(`${minutes}:${seconds} ${minutes === 0 ? 'seconds' : 'minutes'}`);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [timeleft, editableMeal.availability_soon_date_]);

  useEffect(() => setEditableMeal({ ...meal }), [meal]);
  const onActiveChange = () => {
    const pEditableMeal = {
      ...editableMeal,
      altered: true,
      available_no: 'INFINITE',
      availability_soon_date_: null
    };
    setEditableMeal(pEditableMeal);
    onMealEdited(pEditableMeal);
  };
  const updatePrice = (value: string) => {
    const pEditableMeal = {
      ...editableMeal,
      altered: true,
      amount: value
    };
    setEditableMeal(pEditableMeal);
    onMealEdited(pEditableMeal);
  };
  const updateQuantity = (value: string) => {
    const pEditableMeal = {
      ...editableMeal,
      altered: true,
      available_no: value
    };
    setEditableMeal(pEditableMeal);
    onMealEdited(pEditableMeal);
  };
  const handleSwitch = (val: boolean) => {
    if (!val) {
      setShowModal(true);
    } else {
      onActiveChange();
    }
  };

  const submitData = async (soldOutMinutes: number) => {
    try {
      setSubmitting(true);
      const { id: meal_id, meal_category_id } = editableMeal;
      const data = {
        name: editableMeal?.name,
        meal_id,
        meal_category_id,
        availability_soon_date_:
          soldOutMinutes === 0 ? null : moment().add(soldOutMinutes, 'minutes').toDate(),
        available_no: soldOutMinutes === 0 ? '0' : 'INFINITE'
      };
      const res = await apiService.put('marketing/meal', {
        data,
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      setShowModal(false);
      const meal = res.data;
      // delete status and messgae from meal object
      delete meal.status;
      delete meal.message;
      setEditableMeal(meal);
      toast(<ToastMessage type='success' message='Meal set to sold out successfully' />);
    } catch (err) {
      toast(<ToastMessage type='error' message='Error setting meal to sold out' />);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.main}>
      <div className={styles.title}>
        <div className={editableMeal.active ? '' : styles.titleDisabled}>
          {editableMeal.name}
          {editableMeal.is_addon ? <div className={styles.soldOut}>Addon</div> : <></>}
          {editableMeal.is_combo ? <div className={styles.soldOut}>Combo</div> : <></>}
          {+editableMeal.available_no === 0 && editableMeal.availability_soon_date_ == null && (
            <div className={styles.soldOut}>Sold out</div>
          )}
          {editableMeal.availability_soon_date_ !== null &&
            !moment().isAfter(editableMeal.availability_soon_date_) && (
              <div className={styles.soldOut}>Available in {timeleft}</div>
            )}
        </div>
        <div className={styles.detail}>
          <InventoryItemData
            Icon={InventoryItemPriceIcon}
            value={editableMeal.amount}
            edit={false} //updating amount no longer happens here
            onValueChange={updatePrice}
          />
          <InventoryItemData
            Icon={InventoryItemQuantityIcon}
            value={editableMeal.available_no}
            edit={inlineEdit}
            onValueChange={updateQuantity}
          />
        </div>
      </div>
      <div className={styles.actions}>
        <Switch active={active} onChange={handleSwitch} />
        <button className={styles.lineButton} onClick={() => handleMealUpdate(meal)}>
          Edit
        </button>
      </div>
      <TurnOffMeal
        show={showModal}
        onClose={() => setShowModal(false)}
        name={editableMeal.name}
        brandName={brandName}
        workingHours={workingHours}
        onConfirm={submitData}
        loading={submitting}
      />
    </div>
  );
};

export default InventoryItem;
